import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCode, faPlus, faSave, faTrash, faKey, faLink, faDatabase, 
    faSpinner, faCheckCircle, faExclamationCircle, faGlobe, faLock,
    faTerminal, faClipboard, faExternalLinkAlt, faToggleOn, faToggleOff,
    faChevronRight, faChartLine, faHistory, faClock, faMicrochip, faHdd
} from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Spinner, Card, Nav, Tab, Badge } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const ApiPortalPanel = () => {
    const [apis, setApis] = useState([]);
    const [keys, setKeys] = useState([]);
    const [connections, setConnections] = useState([]);
    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApi, setSelectedApi] = useState(null);
    const [activeTab, setActiveTab] = useState('apis');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            new Promise(r => request.Get(API_BASE + 'superadmin/apis', {}, r)),
            new Promise(r => request.Get(API_BASE + 'superadmin/api-keys', {}, r)),
            new Promise(r => request.Get(API_BASE + 'superadmin/connections', {}, r)),
            new Promise(r => request.Get(API_BASE + 'superadmin/queries', {}, r))
        ]).then(([resApis, resKeys, resConns, resQueries]) => {
            if (resApis.success) setApis(resApis.data || []);
            if (resKeys.success) setKeys(resKeys.data || []);
            if (resConns.success) setConnections(resConns.data || []);
            if (resQueries.success) setQueries(resQueries.data || []);
            setLoading(false);
        });
    };

    const handleSaveApi = () => {
        if (!selectedApi.ApiName || !selectedApi.ApiSlug) {
            Notify({ type: 'fail', text: 'Nombre y Slug son requeridos' });
            return;
        }
        const method = selectedApi.ApiId ? 'Put' : 'Post';
        const url = selectedApi.ApiId ? `superadmin/apis/${selectedApi.ApiId}` : 'superadmin/apis';
        
        request[method](API_BASE + url, selectedApi, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'API guardada correctamente' });
                fetchData();
                setSelectedApi(null);
            }
        });
    };

    const handleGenerateKey = () => {
        const name = prompt('Nombre para la nueva API Key:');
        if (!name) return;
        request.Post(API_BASE + 'superadmin/api-keys', { name, active: 1 }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Token generado' });
                fetchData();
            }
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Notify({ type: 'success', text: 'Copiado al portapapeles' });
    };

    if (loading) return <div className="p-5 text-center"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="animate-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="m-0 fw-bold"><FontAwesomeIcon icon={faCode} className="me-2 text-primary" /> API Gateway Management</h5>
                    <p className="sa-text-contrast small m-0">Orquestación de microservicios y exposición de datos segura</p>
                </div>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="pills" className="sa-tabs mb-4">
                    <Nav.Item>
                        <Nav.Link eventKey="apis" className="sa-tab-link"><FontAwesomeIcon icon={faGlobe} className="me-2" /> Endpoints</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="keys" className="sa-tab-link"><FontAwesomeIcon icon={faLock} className="me-2" /> Auth Keys</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="analytics" className="sa-tab-link"><FontAwesomeIcon icon={faChartLine} className="me-2" /> Analíticas Globales</Nav.Link>
                    </Nav.Item>
                    {selectedApi?.ApiId && (
                        <Nav.Item>
                            <Nav.Link eventKey="docs" className="sa-tab-link"><FontAwesomeIcon icon={faTerminal} className="me-2" /> Pruebas (Try-Out)</Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="apis">
                        <Row className="g-4">
                            <Col lg={4}>
                                <div className="d-flex flex-column gap-3">
                                    <button className="sa-btn sa-btn-primary w-100" onClick={() => setSelectedApi({ ApiName: '', ApiSlug: '', ApiActive: 1, ApiResType: 'AUTQ', ApiConnId: '', methods: [{type: 'GET', params: []}] })}>
                                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Crear Nuevo Endpoint
                                    </button>
                                    {apis.map(api => (
                                        <motion.div 
                                            key={api.ApiId}
                                            className={`api-card ${selectedApi?.ApiId === api.ApiId ? 'active' : ''}`}
                                            onClick={() => setSelectedApi(api)}
                                            whileHover={{ x: 5 }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="fw-bold text-white">{api.ApiName}</div>
                                                    <div className="sa-text-contrast small">/api/v1/{api.ApiSlug}</div>
                                                </div>
                                                <div className={`status-pill ${api.ApiActive ? 'active' : 'inactive'}`}>
                                                    {api.ApiActive ? 'On' : 'Off'}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </Col>

                            <Col lg={8}>
                                <AnimatePresence mode="wait">
                                    {selectedApi ? (
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="sa-panel p-4">
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                <h6 className="m-0 fw-bold text-primary">Configuración de Endpoint</h6>
                                                <div className="d-flex gap-2">
                                                    <button className="sa-btn sa-btn-primary" onClick={handleSaveApi}><FontAwesomeIcon icon={faSave} className="me-2" /> Guardar</button>
                                                </div>
                                            </div>

                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <label className="sa-label">Nombre del API</label>
                                                    <input type="text" className="sa-input" value={selectedApi.ApiName} onChange={e => setSelectedApi({...selectedApi, ApiName: e.target.value})} />
                                                </Col>
                                                <Col md={6}>
                                                    <label className="sa-label">Slug (Ruta URL)</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="sa-text-contrast small">/v1/</span>
                                                        <input type="text" className="sa-input" value={selectedApi.ApiSlug} onChange={e => setSelectedApi({...selectedApi, ApiSlug: e.target.value})} />
                                                    </div>
                                                </Col>

                                                <Col md={4}>
                                                    <label className="sa-label">Tipo de Recurso</label>
                                                    <select className="sa-input" value={selectedApi.ApiResType} onChange={e => setSelectedApi({...selectedApi, ApiResType: e.target.value})}>
                                                        <option value="AUTQ">Query (AutQ)</option>
                                                        <option value="TABLE">Tabla Directa</option>
                                                    </select>
                                                </Col>

                                                <Col md={4}>
                                                    <label className="sa-label">Conexión</label>
                                                    <select className="sa-input" value={selectedApi.ApiConnId} onChange={e => setSelectedApi({...selectedApi, ApiConnId: e.target.value})}>
                                                        <option value="">Seleccionar...</option>
                                                        {connections.map(c => <option key={c.ConnId} value={c.ConnId}>{c.ConnName}</option>)}
                                                    </select>
                                                </Col>

                                                <Col md={4}>
                                                    <label className="sa-label">Recurso (ID/Tabla)</label>
                                                    {selectedApi.ApiResType === 'AUTQ' ? (
                                                        <select className="sa-input" value={selectedApi.ApiResId} onChange={e => setSelectedApi({...selectedApi, ApiResId: e.target.value})}>
                                                            <option value="">Seleccionar AutQ...</option>
                                                            {queries.map(q => <option key={q.id} value={q.id}>{q.title} (ID: {q.id})</option>)}
                                                        </select>
                                                    ) : (
                                                        <input type="text" className="sa-input" placeholder="Nombre de tabla" value={selectedApi.ApiResId} onChange={e => setSelectedApi({...selectedApi, ApiResId: e.target.value})} />
                                                    )}
                                                </Col>

                                                <Col md={12}>
                                                    <div className="sa-divider my-4"></div>
                                                    <label className="sa-label mb-3"><FontAwesomeIcon icon={faTerminal} className="me-2 text-primary" /> Métodos y Payloads</label>
                                                    
                                                    <div className="d-flex gap-2 mb-4">
                                                        {['GET', 'POST', 'PUT', 'DELETE'].map(m => (
                                                            <button key={m} type="button" className={`sa-method-btn ${selectedApi.methods?.find(x => x.type === m) ? 'active' : ''}`} onClick={() => {
                                                                const exists = selectedApi.methods?.find(x => x.type === m);
                                                                let newMethods = [...(selectedApi.methods || [])];
                                                                if (exists) newMethods = newMethods.filter(x => x.type !== m);
                                                                else newMethods.push({ type: m, params: [] });
                                                                setSelectedApi({...selectedApi, methods: newMethods});
                                                            }}>{m}</button>
                                                        ))}
                                                    </div>

                                                    <AnimatePresence>
                                                        {(selectedApi.methods || []).map((m, idx) => (
                                                            <motion.div key={m.type} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="method-config-card mb-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <span className="badge bg-primary">{m.type} Configuration</span>
                                                                    <button type="button" className="btn btn-sm btn-link text-primary" onClick={() => {
                                                                        const newMethods = [...selectedApi.methods];
                                                                        newMethods[idx].params.push({ name: '', type: 'string', required: false, location: m.type === 'GET' ? 'query' : 'body' });
                                                                        setSelectedApi({...selectedApi, methods: newMethods});
                                                                    }}><FontAwesomeIcon icon={faPlus} className="me-1" /> Parámetro</button>
                                                                </div>
                                                                {m.params.map((p, pIdx) => (
                                                                    <div key={pIdx} className="param-grid mb-1">
                                                                        <input type="text" className="sa-input sa-input-sm" value={p.name} onChange={e => {
                                                                            const nm = [...selectedApi.methods]; nm[idx].params[pIdx].name = e.target.value; setSelectedApi({...selectedApi, methods: nm});
                                                                        }} placeholder="Key" />
                                                                        <select className="sa-input sa-input-sm" value={p.type} onChange={e => {
                                                                            const nm = [...selectedApi.methods]; nm[idx].params[pIdx].type = e.target.value; setSelectedApi({...selectedApi, methods: nm});
                                                                        }}><option value="string">String</option><option value="number">Number</option></select>
                                                                        <select className="sa-input sa-input-sm" value={p.location} onChange={e => {
                                                                            const nm = [...selectedApi.methods]; nm[idx].params[pIdx].location = e.target.value; setSelectedApi({...selectedApi, methods: nm});
                                                                        }}><option value="query">Query</option><option value="body">Body</option></select>
                                                                        <div className="text-center pt-1"><input type="checkbox" checked={p.required} onChange={e => {
                                                                            const nm = [...selectedApi.methods]; nm[idx].params[pIdx].required = e.target.checked; setSelectedApi({...selectedApi, methods: nm});
                                                                        }} /></div>
                                                                        <FontAwesomeIcon icon={faTrash} className="text-danger cursor-pointer" onClick={() => {
                                                                            const nm = [...selectedApi.methods]; nm[idx].params = nm[idx].params.filter((_, i) => i !== pIdx); setSelectedApi({...selectedApi, methods: nm});
                                                                        }} />
                                                                    </div>
                                                                ))}
                                                            </motion.div>
                                                        ))}
                                                    </AnimatePresence>
                                                </Col>
                                            </Row>
                                        </motion.div>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-100 opacity-50 sa-panel">
                                            <FontAwesomeIcon icon={faLink} className="fs-1 mb-3 text-primary" />
                                            <p>Selecciona un endpoint para configurar su lógica</p>
                                        </div>
                                    )}
                                </AnimatePresence>
                            </Col>
                        </Row>
                    </Tab.Pane>

                    <Tab.Pane eventKey="keys">
                        <div className="sa-panel p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h6 className="m-0 fw-bold text-white">Gestión de Tokens (Bearer)</h6>
                                <button className="sa-btn sa-btn-primary" onClick={handleGenerateKey}><FontAwesomeIcon icon={faKey} className="me-2" /> Generar Nuevo Token</button>
                            </div>
                            <div className="table-responsive">
                                <table className="table sa-table">
                                    <thead><tr><th>Nombre</th><th>Token</th><th>Creado</th><th>Estado</th><th></th></tr></thead>
                                    <tbody>
                                        {keys.map(k => (
                                            <tr key={k.KeyId}>
                                                <td className="fw-bold">{k.KeyName}</td>
                                                <td><code className="opacity-50">{k.KeyToken.substring(0, 15)}...</code> <FontAwesomeIcon icon={faClipboard} className="ms-2 cursor-pointer text-primary" onClick={() => copyToClipboard(k.KeyToken)} /></td>
                                                <td className="small sa-text-contrast">{new Date(k.KeyCreatedAt).toLocaleDateString()}</td>
                                                <td><FontAwesomeIcon icon={k.KeyActive ? faToggleOn : faToggleOff} className={k.KeyActive ? 'text-primary' : 'text-muted'} /></td>
                                                <td className="text-end"><FontAwesomeIcon icon={faTrash} className="text-danger cursor-pointer" onClick={() => { if (window.confirm('¿Borrar?')) request.Delete(API_BASE + `superadmin/api-keys/${k.KeyId}`, {}, fetchData) }} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Tab.Pane>

                    <Tab.Pane eventKey="docs">
                        <ApiDocumentationView api={selectedApi} keys={keys} />
                    </Tab.Pane>

                    <Tab.Pane eventKey="analytics">
                        <ApiAnalyticsView apiId={selectedApi?.ApiId} />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            <style>{`
                .sa-tabs { background: rgba(15, 23, 42, 0.4); padding: 5px; border-radius: 12px; display: inline-flex; }
                .sa-tab-link { border-radius: 8px !important; color: #94a3b8 !important; padding: 8px 20px !important; font-weight: 600; font-size: 0.85rem; border: none !important; }
                .sa-tab-link.active { background: var(--sa-primary) !important; color: white !important; box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3); }
                .api-card { background: rgba(30, 41, 59, 0.4); border: 1px solid rgba(255,255,255,0.08); padding: 16px; border-radius: 12px; cursor: pointer; transition: all 0.2s; }
                .api-card:hover { border-color: var(--sa-primary); background: rgba(168, 85, 247, 0.05); }
                .api-card.active { border-color: var(--sa-primary); background: rgba(168, 85, 247, 0.1); box-shadow: 0 0 20px rgba(168, 85, 247, 0.15); }
                .status-pill { font-size: 0.65rem; font-weight: 800; padding: 2px 8px; border-radius: 20px; text-transform: uppercase; }
                .status-pill.active { background: rgba(16, 185, 129, 0.15); color: #10b981; }
                .status-pill.inactive { background: rgba(148, 163, 184, 0.15); color: #94a3b8; }
                .sa-code-box { background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(168, 85, 247, 0.2); border-radius: 10px; }
                .sa-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent); }
                .sa-method-btn { background: rgba(30, 41, 59, 0.6); border: 1px solid rgba(255,255,255,0.1); color: #94a3b8; padding: 6px 15px; border-radius: 8px; font-weight: 700; font-size: 0.7rem; transition: all 0.2s; margin-right: 5px; }
                .sa-method-btn.active { background: var(--sa-primary); color: white; border-color: var(--sa-primary); }
                .method-config-card { background: rgba(15, 23, 42, 0.3); border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; padding: 12px; }
                .param-grid { display: grid; grid-template-columns: 1fr 100px 100px 40px 30px; gap: 8px; align-items: center; }
                .sa-input-sm { padding: 4px 10px !important; font-size: 0.75rem !important; height: 32px !important; }
                .metric-card { background: rgba(15, 23, 42, 0.5); border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; padding: 20px; }
            `}</style>
        </div>
    );
};

const ApiDocumentationView = ({ api, keys }) => {
    const [spec, setSpec] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [testParams, setTestParams] = useState({});
    const [testToken, setTestToken] = useState('');
    const [response, setResponse] = useState(null);
    const [executing, setExecuting] = useState(false);

    useEffect(() => {
        if (!api?.ApiSlug) return;
        setLoading(true);
        request.Get(API_BASE + `superadmin/apis/${api.ApiSlug}/spec`, {}, r => {
            if (r.success) {
                setSpec(r.data);
                const paths = Object.keys(r.data.paths);
                if (paths.length > 0) {
                    const methods = Object.keys(r.data.paths[paths[0]]);
                    if (methods.length > 0) setSelectedMethod(methods[0].toUpperCase());
                }
            }
            setLoading(false);
        });
    }, [api?.ApiSlug]);

    const handleRun = () => {
        if (!testToken) return Notify({ type: 'fail', text: 'Selecciona una API Key para probar' });
        setExecuting(true);
        setResponse(null);

        const methodDef = api?.methods?.find(m => m.type === selectedMethod);
        const queryParams = methodDef?.params?.filter(p => p.location === 'query') || [];
        const bodyParams = methodDef?.params?.filter(p => p.location === 'body') || [];

        let url = API_BASE.replace(/\/$/, '') + `/api/v1/${api?.ApiSlug}`;
        const qStr = queryParams.map(p => `${p.name}=${encodeURIComponent(testParams[p.name] || '')}`).join('&');
        if (qStr) url += '?' + qStr;

        const body = bodyParams.reduce((acc, p) => { acc[p.name] = testParams[p.name]; return acc; }, {});

        fetch(url, {
            method: selectedMethod,
            headers: { 'Authorization': `Bearer ${testToken}`, 'Content-Type': 'application/json' },
            body: selectedMethod !== 'GET' ? JSON.stringify(body) : undefined
        })
            .then(res => res.json())
            .then(data => setResponse(data))
            .catch(err => setResponse({ error: err.message }))
            .finally(() => setExecuting(false));
    };

    if (!api || loading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <Row className="g-4">
            <Col lg={5}>
                <div className="sa-panel p-4">
                    <h6 className="fw-bold mb-3 text-primary"><FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" /> Consola de Pruebas</h6>
                    <label className="sa-label">Token de Autenticación</label>
                    <select className="sa-input mb-3" value={testToken} onChange={e => setTestToken(e.target.value)}>
                        <option value="">Seleccionar API Key...</option>
                        {keys.filter(k => k.KeyActive).map(k => <option key={k.KeyId} value={k.KeyToken}>{k.KeyName}</option>)}
                    </select>

                    <label className="sa-label">Método a Probar</label>
                    <div className="d-flex gap-2 mb-4">
                        {api.methods.map(m => (
                            <button key={m.type} className={`sa-method-btn ${selectedMethod === m.type ? 'active' : ''}`} onClick={() => setSelectedMethod(m.type)}>{m.type}</button>
                        ))}
                    </div>

                    <div className="mb-4">
                        <label className="sa-label">Parámetros Dinámicos</label>
                        {api.methods.find(m => m.type === selectedMethod)?.params.map(p => (
                            <div key={p.name} className="mb-2">
                                <div className="small text-slate-400 mb-1">{p.name} <span className="opacity-50">({p.location})</span></div>
                                <input type="text" className="sa-input sa-input-sm" placeholder={p.required ? 'Obligatorio...' : 'Opcional...'} onChange={e => setTestParams({ ...testParams, [p.name]: e.target.value })} />
                            </div>
                        ))}
                    </div>
                    <button className="sa-btn sa-btn-primary w-100" onClick={handleRun} disabled={executing}><FontAwesomeIcon icon={executing ? faSpinner : faTerminal} spin={executing} className="me-2" /> Ejecutar Petición</button>
                </div>
            </Col>
            <Col lg={7}>
                <div className="sa-panel p-4 h-100 d-flex flex-column">
                    <h6 className="fw-bold mb-3">Respuesta JSON</h6>
                    <div className="flex-grow-1 sa-code-box p-3 font-monospace small overflow-auto" style={{ maxHeight: '500px', background: '#0a0f18' }}>
                        {response ? <pre className="text-info m-0">{JSON.stringify(response, null, 2)}</pre> : <div className="opacity-20 h-100 d-flex align-items-center justify-content-center text-center">Sin datos de respuesta</div>}
                    </div>
                </div>
            </Col>
        </Row>
    );
};

const ApiAnalyticsView = ({ apiId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/apis/analytics', { apiId }, r => {
            if (r.success) setData(r);
            setLoading(false);
        });
    }, [apiId]);

    if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;

    const { summary, history } = data;

    return (
        <div className="animate-in">
            <Row className="g-3 mb-4">
                <Col md={3}>
                    <div className="metric-card h-100">
                        <div className="small sa-text-contrast mb-1">Peticiones (30d)</div>
                        <div className="fs-3 fw-bold text-white">{summary.totalHits || 0}</div>
                        <div className="small text-primary"><FontAwesomeIcon icon={faChartLine} className="me-1" /> Total Tráfico</div>
                    </div>
                </Col>
                <Col md={3}>
                    <div className="metric-card h-100">
                        <div className="small sa-text-contrast mb-1">Tasa de Éxito</div>
                        <div className="fs-3 fw-bold text-success">
                            {summary.totalHits > 0 ? ((summary.successHits / summary.totalHits) * 100).toFixed(1) : 0}%
                        </div>
                        <div className="small opacity-50">{summary.successHits || 0} exitosas</div>
                    </div>
                </Col>
                <Col md={3}>
                    <div className="metric-card h-100">
                        <div className="small sa-text-contrast mb-1">Errores</div>
                        <div className="fs-3 fw-bold text-danger">{summary.errorHits || 0}</div>
                        <div className="small opacity-50">Status != 200</div>
                    </div>
                </Col>
                <Col md={3}>
                    <div className="metric-card h-100">
                        <div className="small sa-text-contrast mb-1">Latencia Media</div>
                        <div className="fs-3 fw-bold text-info">{Math.round(summary.avgDuration || 0)} ms</div>
                        <div className="small opacity-50"><FontAwesomeIcon icon={faClock} className="me-1" /> Tiempo respuesta</div>
                    </div>
                </Col>
            </Row>

            <div className="sa-panel p-4">
                <h6 className="fw-bold mb-4"><FontAwesomeIcon icon={faHistory} className="me-2 text-primary" /> Historial de Tráfico Reciente</h6>
                <div className="table-responsive">
                    <table className="table sa-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>API</th>
                                <th>Método</th>
                                <th>Status</th>
                                <th>Duración</th>
                                <th>IP</th>
                                <th>Key</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(l => (
                                <tr key={l.LogId}>
                                    <td className="small">{new Date(l.LogCreatedAt).toLocaleString()}</td>
                                    <td className="fw-bold text-white">{l.ApiName}</td>
                                    <td><Badge bg="dark" className="border border-secondary">{l.LogMethod}</Badge></td>
                                    <td>
                                        <Badge bg={l.LogStatus === 200 ? 'success' : 'danger'}>
                                            {l.LogStatus}
                                        </Badge>
                                    </td>
                                    <td className="font-monospace text-info">{l.LogDuration}ms</td>
                                    <td className="small opacity-50">{l.LogIp}</td>
                                    <td className="small text-slate-400">{l.KeyName}</td>
                                </tr>
                            ))}
                            {history.length === 0 && <tr><td colSpan="7" className="text-center p-5 opacity-30">No hay actividad registrada aún</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ApiPortalPanel;
